import { QuestionIcon } from '@phosphor-icons/react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { useQueryClient } from '@tanstack/react-query'
import { getExpandedRowModel, type Row } from '@tanstack/react-table'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { useShallow } from 'zustand/react/shallow'
import {
  isScanAnalysisInProgress,
  useFindingQuickViewState,
  useGetScanAndRepository,
  useGetScanLifecycleState,
  useSearchQueryParam,
  useSortDirectionQueryParam,
  useSortQueryParam,
} from '../../hooks/scan-hooks.ts'
import {
  usePolarisPushPreflight,
  type PolarisPushConfirmationDialogProps,
} from '../../hooks/use-polaris-push-preflight.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  getScanFindingsQueryKeyPrefix,
  invalidateGetPolarisStatuses,
  useGetPolarisStatuses,
  useGetPushesByIds,
  usePostAnalyzeScan,
  usePostPush,
  usePostScanPatch,
} from '../../utils/api-client/api-hooks.ts'
import {
  type FindingAggregateResponse,
  type FindingResponse,
  type IntegrationType,
  type Tool,
} from '../../utils/api-client/api-schemas.ts'
import { isIsoTimestampInPast } from '../../utils/date-utils.ts'
import { isScaTool } from '../../utils/detector-utils.ts'
import {
  getFirstCompletedRepresentativeFixResult,
  getFirstCompletedRepresentativeTriageResult,
  hasExistingPatch,
  hasInProgressPatch,
} from '../../utils/finding-utils.ts'
import { getFirstBatchData, initializeFirstBatch } from '../../utils/first-batch-tracking.ts'
import { getEnvironmentData, type EnvironmentData } from '../../utils/hooks/get-environment-data.ts'
import { getIntegrationName } from '../../utils/repository-utils.tsx'
import { pluralize } from '../../utils/text-utils.ts'
import { PushStatusIcon, type PolarisStatusView } from '../badges/push-status-badge.tsx'
import { useNavigateScanFindingDrawers } from '../drawers/use-navigate-scan-finding-drawers.ts'
import { mapSortDirectionToApi, mapSortOptionToApi } from '../findings-sort-dropdown.tsx'
import { PolarisPushConfirmationPrompt } from '../modals/polaris-push-confirmation-dialog.tsx'
import { type OutcomeReferenceTab } from '../outcomes/outcome-types.ts'
import { OutcomesReferenceDrawer } from '../outcomes/outcomes-reference-drawer.tsx'
import { isInteractiveElementClick } from '../table/row-interaction'
import { BulkActionFeature, FIT_CONTENT_WIDTH, Pagination, Table } from '../table/table'
import * as tableStyles from '../table/table.css'
import { getAdditionalFilterCount, useFilterStateStore, useGetScanFindingsWithFilters } from './filter-state-store.tsx'
import { getFindingQuickViewConfig } from './finding-quick-view-config'
import * as styles from './findings-table.css'
import {
  ClassificationCell,
  ClassificationHeader,
  CvssScoreCell,
  CvssScoreHeader,
  ExpansionToggleCell,
  FIX_CELL_TEST_ID,
  FixCell,
  FixHeader,
  getScaClassificationLabel,
  getSuggestedStatusLabel,
  hasInProgressRepresentativeAnalysis,
  isRowClickable,
  openSubRowDrawer,
  PixeeSeverityCell,
  PixeeSeverityHeader,
  ScaClassificationCell,
  ScaClassificationHeader,
  TitleCell,
  TitleHeader,
  ToolSeverityCell,
  ToolSeverityHeader,
} from './scan-findings-table.cells'
import * as expandStyles from './scan-findings-table.css'
import {
  useGetRowCanExpand,
  useGetSubRows,
  useSubRowsFromEmbeddedData,
  type RowData,
} from './scan-findings-table.hooks'
import { usePagination } from './use-pagination.ts'

export { FIX_CELL_TEST_ID, getScaClassificationLabel, getSuggestedStatusLabel }

// #region Wrapper

export type ScanFindingsTableProps = {
  scanId: string
}

export const ScanFindingsTable: React.FC<ScanFindingsTableProps> = ({ scanId }) => {
  const {
    outcomesReferenceTab,
    onOutcomesReferenceTabChange,
    onCloseOutcomesReferenceDrawer,
    confirmationDialogProps,
    ...props
  } = useScanFindingsTableBase({ scanId })
  return (
    <>
      <ScanFindingsTablePure {...props} />
      <PolarisPushConfirmationPrompt dialogProps={confirmationDialogProps} />
      <OutcomesReferenceDrawer
        open={outcomesReferenceTab !== undefined}
        currentTab={outcomesReferenceTab ?? 'triage'}
        onTabChange={onOutcomesReferenceTabChange}
        onClose={onCloseOutcomesReferenceDrawer}
      />
    </>
  )
}

// #endregion

// #region Hook

type UseScanFindingsTableBaseOptions = {
  scanId: string
}

const buildColumnConfigs = (isSca: boolean): FindingColumnConfig[] =>
  isSca
    ? [
        { type: 'pixeeSeverity', width: FIT_CONTENT_WIDTH },
        { type: 'title', width: '30%' },
        { type: 'toolSeverity', width: FIT_CONTENT_WIDTH },
        { type: 'cvssScore', width: FIT_CONTENT_WIDTH },
        { type: 'scaClassification', width: '12%' },
        { type: 'fix', width: '15%' },
      ]
    : [
        { type: 'pixeeSeverity', width: FIT_CONTENT_WIDTH },
        { type: 'title', width: '40%' },
        { type: 'toolSeverity', width: FIT_CONTENT_WIDTH },
        { type: 'classification', width: '15%' },
        { type: 'fix', width: '15%' },
      ]

function useScanFindingsTableBase({ scanId }: UseScanFindingsTableBaseOptions) {
  const { scan } = useGetScanAndRepository({ scanId })
  const { pagination, onPaginationChange, resetPageIndex } = usePagination({ defaultPageIndex: 0, defaultPageSize: 10 })
  const filterState = useFilterStateStore(useShallow(state => state))
  const [search] = useSearchQueryParam()
  const [selectedSort] = useSortQueryParam()
  const [isAscending] = useSortDirectionQueryParam()
  const { selectedQuickView } = useFindingQuickViewState({ scanId })

  const isInitialRender = useRef(true)
  const filterStateKey = JSON.stringify(filterState)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    resetPageIndex()
  }, [filterStateKey, search, selectedSort, isAscending])

  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  const hasInProgressAnalysis = isScanAnalysisInProgress(scanLifecycleState)

  const { data: paginatedFindings } = useGetScanFindingsWithFilters({
    scanId,
    pagination,
    filterState,
    search: search || undefined,
    sort: mapSortOptionToApi(selectedSort),
    order: mapSortDirectionToApi(isAscending),
    embed: ['items.representative-results.latest-patch', 'items.results.latest-patch'],
    refetchInterval: query => {
      if (hasInProgressAnalysis) return 1000
      if (query.state.data?._embedded.items.some(hasInProgressPatch)) return 1000
      return false
    },
  })
  const {
    onOpenFindingTriageDrawer,
    onOpenFindingFixDrawer,
    onOpenOutcomesReferenceDrawer,
    onOutcomesReferenceTabChange,
    onCloseOutcomesReferenceDrawer,
    findingTriageFindingId,
    findingFixFindingId,
    resultId,
    outcomesReferenceTab,
  } = useNavigateScanFindingDrawers({ scanId })
  const isFirstAnalysis = scanLifecycleState?.type === 'initial-unanalyzed-scan'
  const { handleBulkAnalyze, isPending: isAnalyzePending } = useBulkAnalyzeFindings({
    scanId,
    isFirstAnalysis,
  })
  const { handleBulkCreatePatches, isPending: isCreatePatchesPending } = useBulkCreatePatches({ scanId })
  const scanIntegration = scan?._embedded?.integration
  const integrationName = getIntegrationName(scanIntegration)
  const {
    handleBulkPush,
    isPending: isPushPending,
    confirmationDialogProps,
  } = useBulkPushToIntegration({
    findings: paginatedFindings?._embedded.items ?? [],
    scanId,
    integrationType: scanIntegration?.type,
  })

  const { polarisStatusViews, onRevealPolarisStatus } = usePolarisStatusViews({
    integrationType: scanIntegration?.type,
  })

  const isSca = scan ? isScaTool(scan.detector) : false
  const isPushEligible = scanIntegration?.capabilities?.includes('triage-push') === true
  const isUnanalyzedView = selectedQuickView === 'unanalyzed' || selectedQuickView === 'sca-unanalyzed'
  const scanInputsExpired = isIsoTimestampInPast(scan?.expires_at ?? undefined)
  const scanInputsExpiredTooltip = 'Scan inputs have expired. Run a new scan to reanalyze.'
  const isInitialOrInProgressState =
    scanLifecycleState?.type === 'initial-unanalyzed-scan' ||
    scanLifecycleState?.type === 'first-analysis-batch-in-progress'

  const analyzeButtonConfig = {
    buttonText: 'Analyze',
    buttonType: 'primary' as const,
    onAction: handleBulkAnalyze,
    isPending: isAnalyzePending,
  }

  const reanalyzeButtonConfig = {
    buttonText: 'Reanalyze',
    buttonType: 'secondary' as const,
    onAction: handleBulkAnalyze,
    isPending: isAnalyzePending,
    isDisabled: () => scanInputsExpired,
    disabledTooltip: scanInputsExpiredTooltip,
  }

  const createPrButtonConfig = {
    buttonText: (count: number) => `Create pull ${pluralize(count, 'request', 'requests')}`,
    buttonType: 'primary' as const,
    onAction: handleBulkCreatePatches,
    isDisabled: (data: FindingResponse[], ids: string[]) => !isCreatePrActionActive(data, ids),
    isPending: isCreatePatchesPending,
  }

  const pushButtonConfig = {
    buttonText: integrationName ? `Push classification to ${integrationName}` : 'Push classification',
    buttonType: 'ghost' as const,
    icon: <PushStatusIcon size={16} />,
    onAction: handleBulkPush,
    isDisabled: (data: FindingResponse[], ids: string[]) => {
      const selectedFindings = data.filter(finding => ids.includes(finding.id))
      return (
        selectedFindings.length === 0 ||
        selectedFindings.some(finding => {
          const triageResult = getFirstCompletedRepresentativeTriageResult(finding)
          return (
            triageResult === undefined ||
            triageResult.outcome.suggested_status === null ||
            triageResult.outcome.suggested_status === undefined
          )
        }) ||
        selectedFindings.some(finding => {
          const triageResult = getFirstCompletedRepresentativeTriageResult(finding)
          if (!triageResult) return false
          const findingLatest = finding.push_state
          const pushState =
            triageResult.latest_push_state ??
            (findingLatest?.triage_result_ids.includes(triageResult.id) ? findingLatest : undefined)
          return pushState?.status === 'SUCCESS'
        })
      )
    },
    disabledTooltip:
      'All selected findings must have a completed triage result that has not already been pushed successfully',
    isPending: isPushPending,
  }

  const quickViewConfig = getFindingQuickViewConfig(selectedQuickView)
  const hasActiveFilters = getAdditionalFilterCount(filterState, quickViewConfig.defaultFilterState) > 0

  return {
    emptyStateMessage: hasActiveFilters ? 'No findings match filters' : undefined,
    paginatedFindings: paginatedFindings,
    pagination,
    onPaginationChange,
    columns: buildColumnConfigs(isSca),
    bulkActionFeature: isSca
      ? {
          actionButtonConfigs:
            isUnanalyzedView || isInitialOrInProgressState
              ? [analyzeButtonConfig]
              : [reanalyzeButtonConfig, createPrButtonConfig],
          isRowCheckboxVisible: () => true,
          isRowCheckboxDisabled: finding =>
            !canParticipateInBulkAction(finding, {
              canReanalyze: true,
              canPushRepresentative: false,
            }),
        }
      : {
          actionButtonConfigs:
            isUnanalyzedView || isInitialOrInProgressState
              ? [analyzeButtonConfig]
              : isPushEligible
                ? [pushButtonConfig, reanalyzeButtonConfig, createPrButtonConfig]
                : [reanalyzeButtonConfig, createPrButtonConfig],
          isRowCheckboxVisible: () => true,
          isRowCheckboxDisabled: finding =>
            !canParticipateInBulkAction(finding, {
              canReanalyze: true,
              canPushRepresentative: isPushEligible,
            }),
        },
    selectedFindingId: findingTriageFindingId || findingFixFindingId || undefined,
    selectedResultId: resultId,
    onOpenFindingTriageDrawer,
    onOpenFindingFixDrawer,
    onOpenOutcomesReferenceDrawer,
    isSca,
    integrationName,
    polarisStatusViews,
    onRevealPolarisStatus,
    tool: scan?.detector,
    outcomesReferenceTab,
    onOutcomesReferenceTabChange,
    onCloseOutcomesReferenceDrawer,
    confirmationDialogProps,
  } satisfies ScanFindingsTablePureProps & {
    outcomesReferenceTab: OutcomeReferenceTab | undefined
    onOutcomesReferenceTabChange: (tab: OutcomeReferenceTab) => unknown
    onCloseOutcomesReferenceDrawer: () => unknown
    // Confirmation data, not Pure props: the impure wrapper renders the dialog, so Pure stays
    // drivable from plain data in tests and stories.
    confirmationDialogProps?: PolarisPushConfirmationDialogProps
  }
}

function useBulkAnalyzeFindings({ scanId, isFirstAnalysis }: { scanId: string; isFirstAnalysis: boolean }) {
  const { addToast } = useToastService()

  const showAnalysisError = useShowMutationError<typeof usePostAnalyzeScan>({
    prefix: (_error, variables) => {
      const count = variables.findingIds?.length ?? 0
      return `Failed to trigger analysis for ${count} ${pluralize(count, 'finding', 'findings')}`
    },
  })

  const bulkAnalyzeMutation = usePostAnalyzeScan({
    onSuccess: (_data, variables) => {
      const findingIds = variables.findingIds ?? []
      const count = findingIds.length
      addToast({
        message: (
          <>
            {count} {pluralize(count, 'finding', 'findings')} triggered to analyze
          </>
        ),
        variant: 'success',
      })
    },
    onError: showAnalysisError,
  })

  const handleBulkAnalyze = useCallback(
    (findingIds: string[]) => {
      const hasExistingBatchData = getFirstBatchData(scanId) !== null
      if (isFirstAnalysis || hasExistingBatchData) {
        initializeFirstBatch(scanId, findingIds.length, findingIds)
      }
      return bulkAnalyzeMutation.mutateAsync({ scanId, findingIds })
    },
    [scanId, bulkAnalyzeMutation, isFirstAnalysis]
  )

  return { handleBulkAnalyze, isPending: bulkAnalyzeMutation.isPending }
}

function useBulkCreatePatches({ scanId }: { scanId: string }) {
  const { addToast } = useToastService()

  const showPatchError = useShowMutationError<typeof usePostScanPatch>({
    prefix: (_error, variables) => {
      const count = variables.createScanPatchRequest.findings?.finding_ids.length ?? 0
      return `Failed to create ${pluralize(count, 'patch', 'patches')} for ${count} ${pluralize(count, 'finding', 'findings')}`
    },
  })

  const bulkCreatePatchesMutation = usePostScanPatch({
    scanId,
    onSuccess: (_data, variables) => {
      const count = variables.createScanPatchRequest.findings?.finding_ids.length ?? 0
      addToast({
        message: (
          <>
            {count} {pluralize(count, 'patch', 'patches')} triggered successfully
          </>
        ),
        variant: 'success',
      })
    },
    onError: showPatchError,
  })

  const handleBulkCreatePatches = useCallback(
    (findingIds: string[]) => {
      return bulkCreatePatchesMutation.mutateAsync({
        createScanPatchRequest: { findings: { scan_id: scanId, finding_ids: findingIds } },
      })
    },
    [scanId, bulkCreatePatchesMutation.mutateAsync]
  )

  return { handleBulkCreatePatches, isPending: bulkCreatePatchesMutation.isPending }
}

/**
 * Owns the hover-gated Polaris status lookup for the findings table's push badges.
 *
 * Lives here rather than in `PushStatusBadge` so the badges stay renderable from plain props and
 * `ScanFindingsTablePure`'s subtree issues no requests. Only the badge that was last reached is asked
 * about, so a table of findings costs one request rather than one per row: the badges report which one
 * that is through `onRevealPolarisStatus`.
 */
function usePolarisStatusViews({ integrationType }: { integrationType?: IntegrationType }) {
  const [revealedTriageResultId, setRevealedTriageResultId] = useState<string>()
  const isPolarisScan = integrationType === 'polaris'
  const {
    data: lookup,
    isPending,
    isError,
  } = useGetPolarisStatuses({
    triageResultIds: revealedTriageResultId ? [revealedTriageResultId] : [],
    enabled: isPolarisScan && revealedTriageResultId !== undefined,
  })

  const polarisStatusViews = useMemo(() => {
    if (revealedTriageResultId === undefined) {
      return undefined
    }
    const view = match({ isError, isPending, lookup })
      .with({ isError: true }, (): PolarisStatusView => ({ state: 'unavailable' }))
      .with({ isPending: true }, (): PolarisStatusView => ({ state: 'pending' }))
      .otherwise(({ lookup: response }): PolarisStatusView => {
        // One target was asked about, so the response reports it either as a status or as skipped. The
        // skip is worth saying out loud: it is the difference between "Polaris agrees" and "we never
        // asked".
        const status = response?.statuses[0]
        const skipped = response?.skipped[0]
        if (status) {
          return { state: 'resolved', status }
        }
        return skipped ? { state: 'skipped', skipped } : { state: 'unavailable' }
      })
    return { [revealedTriageResultId]: view }
  }, [isError, isPending, lookup, revealedTriageResultId])

  return {
    polarisStatusViews,
    // Absent for non-Polaris scans, which is what leaves those badges inert rather than focusable with
    // a tooltip that never fills in.
    onRevealPolarisStatus: isPolarisScan ? setRevealedTriageResultId : undefined,
  }
}

function useBulkPushToIntegration({
  findings,
  scanId,
  integrationType,
}: {
  findings: FindingResponse[]
  scanId: string
  integrationType?: IntegrationType
}) {
  const { addToast } = useToastService()
  const queryClient = useQueryClient()
  const [trackedPushIds, setTrackedPushIds] = useState<string[] | null>(null)

  const showPushError = useShowMutationError<typeof usePostPush>({
    prefix: (_error, variables) => {
      const count = variables.triageResultIds.length
      return count === 1 ? 'Error pushing classification' : `Error pushing ${count} classifications`
    },
  })

  const pushMutation = usePostPush({
    onSuccess: data => {
      const enqueued = data.pushes.length
      const skipped = data.skipped.length
      addToast({
        message: (
          <>
            Queued {enqueued} {pluralize(enqueued, 'classification', 'classifications')} for push
            {skipped > 0 ? ` (${skipped} skipped)` : ''}
          </>
        ),
        variant: 'info',
      })
      if (enqueued > 0) {
        setTrackedPushIds(data.pushes.map(p => p.id))
        queryClient.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
      }
    },
    onError: showPushError,
  })

  // Only Polaris carries remote triage state a push could overwrite, so the preflight is gated on the
  // integration's type rather than its display label.
  const { preflight, confirmationDialogProps, isPushPending } = usePolarisPushPreflight({
    integrationType,
    push: pushMutation.mutateAsync,
  })

  const { data: pushesData } = useGetPushesByIds({
    ids: trackedPushIds ?? [],
    enabled: trackedPushIds !== null,
  })

  useEffect(() => {
    if (trackedPushIds === null || pushesData === undefined) {
      return
    }
    const allTerminal =
      pushesData.pushes.length > 0 &&
      pushesData.pushes.every(push => push.status === 'SUCCESS' || push.status === 'FAILED')
    if (!allTerminal) {
      return
    }
    queryClient.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
    invalidateGetPolarisStatuses(queryClient)
    const succeededPushes = pushesData.pushes.filter(push => push.status === 'SUCCESS')
    const failedPushes = pushesData.pushes.filter(push => push.status === 'FAILED')
    const succeededCount = succeededPushes.flatMap(push => push.triage_result_ids).length
    const failedCount = failedPushes.flatMap(push => push.triage_result_ids).length
    const firstFailureReason = failedPushes.find(push => push.error_reason)?.error_reason
    addToast({
      message:
        failedCount === 0 ? (
          <>
            {succeededCount === 1
              ? 'Classification successfully pushed'
              : `${succeededCount} classifications successfully pushed`}
          </>
        ) : (
          <>
            {failedCount === 1 ? 'Error pushing classification' : `Error pushing ${failedCount} classifications`}
            {firstFailureReason ? `: ${firstFailureReason}` : ''}
          </>
        ),
      variant: failedCount === 0 ? 'success' : 'error',
    })
    setTrackedPushIds(null)
  }, [trackedPushIds, pushesData, addToast, queryClient, scanId])

  const findingsById = useMemo(() => new Map(findings.map(f => [f.id, f])), [findings])

  const handleBulkPush = useCallback(
    async (findingIds: string[]) => {
      const triageResultIds = findingIds
        .map(id => findingsById.get(id))
        .map(finding => (finding ? getFirstCompletedRepresentativeTriageResult(finding)?.id : undefined))
        .filter((id): id is string => Boolean(id))

      return preflight({ triageResultIds })
    },
    [findingsById, preflight]
  )

  return {
    handleBulkPush,
    isPending: isPushPending,
    confirmationDialogProps,
  }
}

// #endregion

// #region Pure Component

type BuildColumnDefinitionsOptions = {
  columns: FindingColumnConfig[]
  environmentData: EnvironmentData
  areRowsSelectable: boolean
  tool?: Tool
  isSca: boolean
  integrationName?: string
  /**
   * What Polaris says about each finding whose push badge has been reached, keyed by triage result ID.
   *
   * Supplied by the impure wrapper: the badges are hover-gated, so only one entry is ever populated,
   * and this table's subtree stays free of requests.
   */
  polarisStatusViews?: Record<string, PolarisStatusView>
  /** Starts the Polaris lookup for one finding. Absent when the scan's integration is not Polaris. */
  onRevealPolarisStatus?: (triageResultId: string) => unknown
  onOpenFindingTriageDrawer: (findingId: string | undefined, resultId?: string) => unknown
  onOpenFindingFixDrawer: (findingId: string | undefined, resultId?: string) => unknown
  onOpenOutcomesReferenceDrawer: (tab: OutcomeReferenceTab) => unknown
}

const buildColumnDefinitions = ({
  columns,
  environmentData,
  areRowsSelectable,
  tool,
  isSca,
  integrationName,
  polarisStatusViews,
  onRevealPolarisStatus,
  onOpenFindingTriageDrawer,
  onOpenFindingFixDrawer,
  onOpenOutcomesReferenceDrawer,
}: BuildColumnDefinitionsOptions) => {
  const dataColumns = columns.map(config => {
    const baseColumn = match(config.type)
      .with('pixeeSeverity', type => ({
        accessorKey: type,
        header: () => <PixeeSeverityHeader environmentData={environmentData} />,
        cell: ({ row }: { row: Row<RowData> }) => <PixeeSeverityCell row={row} />,
      }))
      .with('title', type => ({
        accessorKey: type,
        header: () => <TitleHeader />,
        cell: ({ row }: { row: Row<RowData> }) => <TitleCell row={row} areRowsSelectable={areRowsSelectable} />,
      }))
      .with('toolSeverity', type => ({
        accessorKey: type,
        header: () => <ToolSeverityHeader tool={tool} />,
        cell: ({ row }: { row: Row<RowData> }) => <ToolSeverityCell row={row} />,
      }))
      .with('cvssScore', type => ({
        accessorKey: type,
        header: () => <CvssScoreHeader environmentData={environmentData} />,
        cell: ({ row }: { row: Row<RowData> }) => <CvssScoreCell row={row} />,
      }))
      .with('classification', type => ({
        accessorKey: type,
        header: () => <ClassificationHeader environmentData={environmentData} />,
        cell: ({ row }: { row: Row<RowData> }) => (
          <ClassificationCell
            row={row}
            onOpenFindingTriageDrawer={onOpenFindingTriageDrawer}
            integrationName={integrationName}
            polarisStatusViews={polarisStatusViews}
            onRevealPolarisStatus={onRevealPolarisStatus}
          />
        ),
      }))
      .with('scaClassification', type => ({
        accessorKey: type,
        header: () => <ScaClassificationHeader environmentData={environmentData} />,
        cell: ({ row }: { row: Row<RowData> }) => (
          <ScaClassificationCell row={row} onOpenFindingTriageDrawer={onOpenFindingTriageDrawer} />
        ),
      }))
      .with('fix', type => ({
        accessorKey: type,
        header: () => <FixHeader environmentData={environmentData} />,
        cell: ({ row }: { row: Row<RowData> }) => (
          <div
            data-testid={FIX_CELL_TEST_ID}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: row.original.type === 'non-representative' ? '36px' : undefined,
            }}
          >
            <FixCell row={row} onSelect={onOpenFindingFixDrawer} onOpenFindingFixDrawer={onOpenFindingFixDrawer} />
          </div>
        ),
      }))
      .exhaustive()

    return {
      ...baseColumn,
      meta: { width: config.width },
    }
  })

  return [
    ...dataColumns,
    {
      id: 'expand',
      header: () => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <OutcomesReferenceDrawerTrigger isSca={isSca} onOpen={onOpenOutcomesReferenceDrawer} />
        </div>
      ),
      cell: ({ row }: { row: Row<RowData> }) => <ExpansionToggleCell row={row} />,
      meta: { width: FIT_CONTENT_WIDTH },
    },
  ]
}

export type FindingColumnType =
  'pixeeSeverity' | 'toolSeverity' | 'cvssScore' | 'title' | 'classification' | 'fix' | 'scaClassification'

export type FindingColumnConfig = {
  type: FindingColumnType
  width: string
}

export type ScanFindingsTablePureProps = {
  /*
   * The tool is required to display the tool icon in the severity header.
   * Optional because the tool needs to load.
   */
  tool?: Tool
  paginatedFindings?: FindingAggregateResponse
  pagination: Pagination
  onPaginationChange: (pagination: Pagination) => unknown
  selectedFindingId?: string
  selectedResultId?: string
  onOpenFindingTriageDrawer?: (findingId: string | undefined, resultId?: string) => unknown
  onOpenFindingFixDrawer?: (findingId: string | undefined, resultId?: string) => unknown
  /** Opens the Outcomes Reference Drawer with a default tab. Required for the documentation column header. */
  onOpenOutcomesReferenceDrawer?: (tab: OutcomeReferenceTab) => unknown
  /** When true, the documentation column header defaults the drawer to the SCA tab; otherwise to Triage. */
  isSca?: boolean
  columns: FindingColumnConfig[]
  areRowsSelectable?: boolean
  bulkActionFeature?: BulkActionFeature<FindingResponse>
  emptyStateMessage?: string
  integrationName?: string
  /**
   * What Polaris says about each finding whose push badge has been reached, keyed by triage result ID.
   *
   * The badge lookups are hover-gated and owned by the impure wrapper, so this table never issues one
   * itself and only one entry is ever populated.
   */
  polarisStatusViews?: Record<string, PolarisStatusView>
  /** Starts the Polaris lookup for one finding. Absent when the scan's integration is not Polaris. */
  onRevealPolarisStatus?: (triageResultId: string) => unknown
}

export const ScanFindingsTablePure: React.FC<ScanFindingsTablePureProps> = ({
  tool,
  paginatedFindings,
  pagination,
  onPaginationChange,
  selectedFindingId,
  selectedResultId,
  onOpenFindingTriageDrawer = () => {},
  onOpenFindingFixDrawer = () => {},
  onOpenOutcomesReferenceDrawer = () => {},
  isSca = false,
  columns,
  areRowsSelectable = true,
  bulkActionFeature,
  emptyStateMessage,
  integrationName,
  polarisStatusViews,
  onRevealPolarisStatus,
}) => {
  const environmentData = getEnvironmentData()
  const findings = paginatedFindings?._embedded.items ?? []

  const subRowsMap = useSubRowsFromEmbeddedData(findings)
  const getSubRows = useGetSubRows(subRowsMap)
  const getRowCanExpand = useGetRowCanExpand(subRowsMap)

  const columnDefinitions = useMemo(
    () =>
      buildColumnDefinitions({
        columns,
        environmentData,
        areRowsSelectable,
        tool,
        isSca,
        integrationName,
        polarisStatusViews,
        onRevealPolarisStatus,
        onOpenFindingTriageDrawer,
        onOpenFindingFixDrawer,
        onOpenOutcomesReferenceDrawer,
      }),
    [
      columns,
      environmentData,
      onOpenFindingFixDrawer,
      onOpenFindingTriageDrawer,
      onOpenOutcomesReferenceDrawer,
      isSca,
      areRowsSelectable,
      tool,
      integrationName,
      polarisStatusViews,
      onRevealPolarisStatus,
    ]
  )

  const tableData = useMemo(
    () =>
      paginatedFindings?._embedded.items.map(finding => ({
        type: 'outer' as const,
        data: finding,
      })) ?? [],
    [paginatedFindings, getSubRows]
  )

  const adaptedBulkActionFeature = useMemo<BulkActionFeature<RowData> | undefined>(() => {
    if (!bulkActionFeature) return undefined
    return {
      actionButtonConfigs: bulkActionFeature.actionButtonConfigs.map(action => ({
        ...action,
        isDisabled: action.isDisabled
          ? (data: RowData[], selectedIds: string[]) => {
              const findings = data.filter(row => row.type === 'outer').map(row => row.data)
              return action.isDisabled?.(findings, selectedIds) ?? false
            }
          : undefined,
        disabledTooltip: typeof action.disabledTooltip === 'function' ? undefined : action.disabledTooltip,
      })),
      isRowCheckboxVisible: (row: RowData) => row.type === 'outer' && bulkActionFeature.isRowCheckboxVisible(row.data),
      isRowCheckboxDisabled: (row: RowData) => {
        if (row.type !== 'outer') return false
        return bulkActionFeature.isRowCheckboxDisabled?.(row.data) ?? false
      },
    }
  }, [bulkActionFeature])

  const outerRowIndices = useMemo(() => {
    const map = new Map<string, number>()
    let index = 0
    for (const row of tableData) {
      if (row.type === 'outer') {
        map.set(row.data.id, index)
        index++
      }
    }
    return map
  }, [tableData])

  const getRowClassName = useCallback(
    (row: Row<RowData>) => {
      if (row.depth === 0 && row.original.type === 'outer') {
        const isSelectable = isRowClickable(row.original.data, areRowsSelectable)
        const isExpandable = row.getCanExpand()
        const isExpanded = isExpandable && row.getIsExpanded()

        const outerRowIndex = outerRowIndices.get(row.original.data.id) ?? 0
        const isOddRow = outerRowIndex % 2 === 0

        const isSelectedFinding = row.original.data.id === selectedFindingId
        const shouldDimOuterRow = isSelectedFinding && !!selectedResultId

        return classNames({
          [styles.selectableRow]: isSelectable,
          [styles.checkboxSelectedRow]: row.getIsSelected(),
          [expandStyles.expandedParentRow]: isExpanded,
          [tableStyles.oddRowExplicit]: isOddRow,
          [tableStyles.evenRowExplicit]: !isOddRow,
          [expandStyles.nonRepresentativeSubRowOfSelected]: shouldDimOuterRow,
        })
      }

      const parentRow = row.getParentRow()
      if (parentRow && parentRow.original.type === 'outer') {
        const parentOuterRowIndex = outerRowIndices.get(parentRow.original.data.id) ?? 0
        const isParentOdd = parentOuterRowIndex % 2 === 0

        const subRows = parentRow.subRows ?? []
        const isLastSubRow = subRows.length > 0 && row.id === subRows[subRows.length - 1]?.id

        const subRowType = row.original.type
        const subRowClass =
          subRowType === 'representative' ? expandStyles.representativeSubRow : expandStyles.nonRepresentativeSubRow

        const isSubRowOfSelectedFinding = row.getParentRow()?.id === selectedFindingId

        let isHighlighted = false
        let isDimmed = false

        if (isSubRowOfSelectedFinding && row.original.type !== 'outer') {
          const subRowData = row.original.data
          const matchesSelectedResult = selectedResultId
            ? match(subRowData)
                .with(
                  { type: 'sast' },
                  sast => sast.triage?.id === selectedResultId || sast.fix?.id === selectedResultId
                )
                .with({ type: 'sca' }, sca => sca.sca?.id === selectedResultId)
                .exhaustive()
            : subRowType === 'representative'

          isHighlighted = matchesSelectedResult
          isDimmed = !matchesSelectedResult
        }

        return classNames(
          subRowClass,
          isLastSubRow ? expandStyles.expandedNestedRowLast : expandStyles.expandedNestedRow,
          isParentOdd ? tableStyles.oddRowExplicit : tableStyles.evenRowExplicit,
          {
            [expandStyles.representativeSubRowNoDeemphasis]: isHighlighted,
            [expandStyles.nonRepresentativeSubRowOfSelected]: isDimmed,
          }
        )
      }

      return ''
    },
    [outerRowIndices, areRowsSelectable, selectedFindingId, selectedResultId]
  )

  const handleRowClick = useCallback(
    (row: Row<RowData>, event: React.MouseEvent<HTMLTableRowElement> | React.KeyboardEvent<HTMLTableRowElement>) => {
      if (isInteractiveElementClick(event)) {
        return
      }

      match(row.original)
        .with({ type: 'outer' }, outerRow => {
          if (isRowClickable(outerRow.data, areRowsSelectable)) {
            onOpenFindingTriageDrawer(outerRow.data.id)
          }
        })
        .with({ type: 'representative' }, subRow => {
          openSubRowDrawer(subRow.data, onOpenFindingTriageDrawer, onOpenFindingFixDrawer)
        })
        .with({ type: 'non-representative' }, subRow => {
          openSubRowDrawer(subRow.data, onOpenFindingTriageDrawer, onOpenFindingFixDrawer)
        })
        .exhaustive()
    },
    [areRowsSelectable, onOpenFindingTriageDrawer, onOpenFindingFixDrawer]
  )

  return (
    <Table<RowData>
      data={tableData}
      columns={columnDefinitions}
      getRowId={row => (row.type === 'outer' ? row.data.id : `${row.data.findingId}-${row.data.analysisHref}`)}
      isLoading={paginatedFindings === undefined}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      tableName="Findings"
      totalItems={paginatedFindings?.total}
      getRowClassName={getRowClassName}
      highlightedRowId={selectedFindingId}
      bulkActionFeature={adaptedBulkActionFeature}
      getExpandedRowModel={getExpandedRowModel()}
      getSubRows={getSubRows}
      getRowCanExpand={getRowCanExpand}
      onRowClick={handleRowClick}
      emptyStateMessage={emptyStateMessage}
    />
  )
}

export const isCreatePrActionActive = (findings: FindingResponse[], selectedIds: string[]): boolean => {
  const selectedFindings = findings.filter(f => selectedIds.includes(f.id))
  return selectedFindings.every(
    finding => getFirstCompletedRepresentativeFixResult(finding) !== undefined && !hasExistingPatch(finding)
  )
}

export type OutcomesReferenceDrawerTriggerProps = {
  /** SCA scans default the drawer to the SCA tab; everything else defaults to Triage. */
  isSca: boolean
  onOpen: (tab: OutcomeReferenceTab) => unknown
}

export const OutcomesReferenceDrawerTrigger: React.FC<OutcomesReferenceDrawerTriggerProps> = ({ isSca, onOpen }) => {
  const defaultTab: OutcomeReferenceTab = isSca ? 'sca' : 'triage'
  return (
    <RadixTooltip.Provider delayDuration={0}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>
          <button
            type="button"
            aria-label="View outcome documentation"
            className={expandStyles.docTriggerButton}
            onClick={() => onOpen(defaultTab)}
          >
            <QuestionIcon className={expandStyles.docTriggerIcon} aria-hidden="true" />
          </button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content className={expandStyles.docTriggerTooltip} side="bottom" sideOffset={4}>
            View documentation
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

export const canParticipateInBulkAction = (
  finding: FindingResponse,
  scanLevel: { canReanalyze: boolean; canPushRepresentative: boolean }
): boolean => {
  const isReanalyzable = scanLevel.canReanalyze && !hasInProgressRepresentativeAnalysis(finding)
  const representativeTriageResult = getFirstCompletedRepresentativeTriageResult(finding)
  const isPushable =
    scanLevel.canPushRepresentative &&
    representativeTriageResult !== undefined &&
    representativeTriageResult.outcome.suggested_status !== null &&
    representativeTriageResult.outcome.suggested_status !== undefined
  const isFixable = getFirstCompletedRepresentativeFixResult(finding) !== undefined && !hasExistingPatch(finding)
  return isReanalyzable || isPushable || isFixable
}

// #endregion
